import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useBlocker } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ReportTypesPanel } from "../../components/ReportTypesPanel"
import { SummaryLayoutPanel } from "../../components/SummaryLayoutPanel"
import { useAuth } from "../../hooks/useAuth"
import { useCanManageSettings } from "../../hooks/useCanManageSettings"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  clearTenantSettingOverrides,
  fetchAdapterCircuitBreaker,
  fetchAdapterKeyStatus,
  fetchAdminAdapterKeyStatus,
  fetchAdminSettings,
  fetchSettings,
  updateAdminSettings,
  updateCompanyBranding,
  updateSettings,
} from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { authClient } from "../../lib/auth-client"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { useProduct, useTenant } from "../../lib/tenant"
import {
  BATCH_ADAPTERS,
  parseFallbackOrder,
  REDACTED_SECRET,
  STREAMING_ADAPTERS,
  type TBatchAdapter,
  type TCircuitState,
  type TStreamingAdapter,
} from "./-settings/constants"
import { SettingsView } from "./-settings/SettingsView"
import type { IBrandingDraft } from "./-settings/SettingsView/interfaces"

function SettingsPage(): React.JSX.Element {
  const { user } = useAuth()
  const company = useCompanyApi()
  const orgId = user?.activeOrganizationId ?? null
  const product = useProduct()
  const { branding, mcpUrl, loading: tenantLoading } = useTenant()
  // Same name the header shows beside the logo: host branding, then the active
  // workspace. Host branding is null on localhost and on the admin host.
  const { data: activeOrg } = authClient.useActiveOrganization()
  const { i18n } = useLingui()
  const [activeTab, setActiveTab] = useState<string>("general")
  const isAdminOnly = !company && !!user?.isAdmin
  // Owner/admin (or super-admin) may write settings; a plain member is read-only.
  // Mirrors the backend PUT /settings gate so members never click into a 403.
  const canManage = useCanManageSettings()
  // Appearance (company default theme) is only meaningful for a real Lexicanon
  // company — not the super-admin platform-only view, and not Voxcanon.
  const showAppearance = !isAdminOnly && !!company && product === "lexicanon"

  const queryClient = useQueryClient()
  const [settings, setSettings] = useState<Record<string, string>>({})
  const [initialized, setInitialized] = useState(false)
  /** Snapshot of form state after first paint (Select/NumberInput mount writes). */
  const [hydratedBaseline, setHydratedBaseline] = useState<Record<string, string> | null>(null)
  // Keys whose stored secret the backend masked as REDACTED_SECRET. The client
  // never receives the real value, so these are "configured but unknown": the
  // form holds "" for them (so the input renders blank, not the sentinel) and
  // the provider cards show a "Configured" badge instead of "Not configured".
  const [configuredSecrets, setConfiguredSecrets] = useState<Set<string>>(new Set())

  const [batchOrder, setBatchOrder] = useState<TBatchAdapter[]>(BATCH_ADAPTERS)
  const [streamingOrder, setStreamingOrder] = useState<TStreamingAdapter[]>(STREAMING_ADAPTERS)

  const {
    isLoading,
    error,
    data: settingsData,
  } = useQuery({
    queryKey: queryKeys.settings(orgId),
    queryFn: () => (isAdminOnly || !company ? fetchAdminSettings() : fetchSettings(company)),
    enabled: isAdminOnly || !!company,
  })

  useEffect(() => {
    if (settingsData && !initialized) {
      // Seed at the masking seam: a key returned as REDACTED_SECRET is
      // configured-but-unknown — blank the form value and record it as
      // configured. A real, never-masked value seeds through unchanged.
      const seeded: Record<string, string> = {}
      const configured = new Set<string>()
      for (const [key, value] of Object.entries(settingsData)) {
        if (value === REDACTED_SECRET) {
          seeded[key] = ""
          configured.add(key)
        } else {
          seeded[key] = value
        }
      }
      setSettings(seeded)
      setConfiguredSecrets(configured)
      setBatchOrder(parseFallbackOrder<TBatchAdapter>(settingsData.batch_fallback_order, BATCH_ADAPTERS))
      setStreamingOrder(
        parseFallbackOrder<TStreamingAdapter>(settingsData.streaming_fallback_order, STREAMING_ADAPTERS),
      )
      setInitialized(true)
      setHydratedBaseline(null)
    }
  }, [settingsData, initialized])

  // Mantine Select/NumberInput write displayed defaults on mount for unset keys,
  // including panels that only mount when a settings group tab is selected.
  // Snapshot after idle so those writes are not "Unsaved changes".
  useEffect(() => {
    if (!initialized) return
    const snap = (): void => {
      setHydratedBaseline((prev) => prev ?? { ...settings })
    }
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(snap, { timeout: 400 })
      return () => window.cancelIdleCallback(id)
    }
    const id = window.setTimeout(snap, 300)
    return () => window.clearTimeout(id)
  }, [initialized, settings])

  // A newly selected settings group mounts more controls, which write defaults
  // for keys the first snapshot never saw. Absorb those keys as still-clean.
  useEffect(() => {
    if (!initialized || !hydratedBaseline) return
    const id = window.setTimeout(() => {
      setHydratedBaseline((prev) => {
        if (!prev) return prev
        let changed = false
        const next = { ...prev }
        for (const [k, v] of Object.entries(settings)) {
          if (!(k in next)) {
            next[k] = v
            changed = true
          }
        }
        return changed ? next : prev
      })
    }, 300)
    return () => window.clearTimeout(id)
  }, [initialized, hydratedBaseline, settings])

  // Live failover health for the Provider-order panel's per-provider badges.
  // Company-scoped (useCompanyQuery auto-disables when there's no active
  // company, i.e. the super-admin platform view), polled every 60s. Per-pod
  // state — the copy on the panel says the health is "as seen by this server".
  const { data: healthData } = useCompanyQuery({
    key: queryKeys.adapterCircuitBreaker,
    queryFn: (client) => fetchAdapterCircuitBreaker(client),
    refetchInterval: 60_000,
  })
  // A provider → circuit-state map for the panel. `undefined` in the platform
  // admin view (no company context) so the chains render without health badges;
  // the platform admin has the Diagnostics page for global failover health.
  const providerHealth = useMemo<Record<string, TCircuitState> | undefined>(() => {
    if (isAdminOnly) return undefined
    const map: Record<string, TCircuitState> = {}
    for (const p of healthData?.providers ?? []) map[p.provider] = p.state
    return map
  }, [isAdminOnly, healthData])

  // Per-provider "does a key actually resolve" truth (org BYOK → platform →
  // env). The chain resolver SILENTLY skips keyless providers, so the panel
  // must warn where the runtime will skip — a "Platform key" assumption is not
  // a resolution check (2026-07-22: soniox shown as primary while its pruned
  // env key made every live session fall through to deepgram unannounced).
  const { data: keyStatusData } = useCompanyQuery({
    key: queryKeys.adapterKeyStatus,
    queryFn: (client) => fetchAdapterKeyStatus(client),
    refetchInterval: 60_000,
  })
  // Platform-scope resolution for the super-admin view (no company context).
  // Without it the admin badges fell back to "does a platform_<name>_key
  // settings row exist" — false-negative "Not set" for every env-provided key.
  const { data: adminKeyStatusData } = useQuery({
    queryKey: queryKeys.adapterKeyStatus(null),
    queryFn: fetchAdminAdapterKeyStatus,
    refetchInterval: 60_000,
    enabled: isAdminOnly,
  })
  const resolvedKeys = useMemo<Record<string, boolean> | undefined>(() => {
    const data = isAdminOnly ? adminKeyStatusData : keyStatusData
    if (data === undefined) return undefined
    const map: Record<string, boolean> = {}
    for (const p of data.providers) map[p.name] = p.hasKey
    return map
  }, [isAdminOnly, keyStatusData, adminKeyStatusData])

  const updateSetting = useCallback((key: string, value: string): void => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }, [])

  // Dirty-state guard — the settings page spans five groups and many panels save
  // through different code paths (save / saveKey / saveFallback). If a user
  // edits the analysis prompt, switches groups, and accidentally clicks away in
  // the nav, they lose their edits silently. Track whether the in-memory
  // `settings` differs from the server-loaded `settingsData` and warn on both
  // browser-level unload AND in-app router navigation.
  const isDirty = useMemo<boolean>(() => {
    if (!hydratedBaseline) return false
    const keys = new Set<string>([...Object.keys(hydratedBaseline), ...Object.keys(settings)])
    for (const k of keys) {
      if ((hydratedBaseline[k] ?? "") !== (settings[k] ?? "")) return true
    }
    return false
  }, [hydratedBaseline, settings])

  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [isDirty])

  useBlocker({
    shouldBlockFn: () => {
      if (!isDirty) return false
      return !window.confirm(i18n._(msg`You have unsaved settings. Leave anyway?`))
    },
    enableBeforeUnload: false,
  })

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: (keys?: string[]) => {
      const toSave: Record<string, string> = {}
      for (const [key, value] of Object.entries(settings)) {
        if (keys && !keys.includes(key)) continue
        // A configured secret the user never retyped holds "" in form state.
        // Sending "" would CLEAR the stored key — so skip it entirely and keep
        // the saved value. Only a freshly-typed value (non-empty) replaces it.
        if (configuredSecrets.has(key) && value === "") continue
        toSave[key] = value
      }
      // Nothing real to send — every requested key was an untouched configured
      // secret. Resolve a sentinel result so onSuccess can say "No changes"
      // instead of falsely claiming a save happened.
      if (Object.keys(toSave).length === 0) return Promise.resolve({ success: true, noChanges: true })
      if (isAdminOnly) return updateAdminSettings(toSave)
      if (!company) return Promise.resolve({ success: false })
      return updateSettings(company, toSave)
    },
    onSuccess: (result) => {
      const noChanges = typeof result === "object" && result !== null && "noChanges" in result
      notifications.show({
        title: noChanges ? i18n._(msg`No changes`) : i18n._(msg`Settings saved`),
        message: noChanges
          ? i18n._(msg`The saved key is unchanged — enter a new value to replace it.`)
          : i18n._(msg`Changes applied`),
        color: noChanges ? "info" : "success",
      })
      if (!noChanges) {
        setHydratedBaseline({ ...settings })
        queryClient.invalidateQueries({ queryKey: queryKeys.settings(orgId) })
      }
    },
    onError: (err) => notifyError(err),
  })

  const saveKey = useCallback(
    (key: string): void => {
      save([key])
    },
    [save],
  )

  const { mutate: saveFallback, isPending: savingFallback } = useMutation({
    mutationFn: (payload: Record<string, string>) => {
      if (isAdminOnly) return updateAdminSettings(payload)
      if (!company) return Promise.resolve({ success: false })
      return updateSettings(company, payload)
    },
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Settings saved`),
        message: i18n._(msg`Changes applied`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.settings(orgId) })
    },
    onError: (err) => notifyError(err),
  })

  // Super-admin "apply to every workspace": removes each org's own chain
  // override rows so every tenant resolves the platform order (now and for
  // future platform changes). Explicit confirm — it discards tenant choices.
  const { mutate: clearTenantOrderOverrides, isPending: applyingOrderToAllTenants } = useMutation({
    mutationFn: () => clearTenantSettingOverrides(["batch_fallback_order", "streaming_fallback_order"]),
    onSuccess: ({ cleared }) => {
      const total = Object.values(cleared).reduce((sum, n) => sum + n, 0)
      notifications.show({
        title: i18n._(msg`Applied to every workspace`),
        message:
          total === 0
            ? i18n._(msg`No workspace had its own provider order — everyone already follows the platform order.`)
            : i18n._(msg`Removed ${total} workspace override(s) — every workspace now follows the platform order.`),
        color: "success",
      })
    },
    onError: (err) => notifyError(err),
  })

  const confirmApplyOrderToAllTenants = useCallback((): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Apply provider order to every workspace?`),
      children: i18n._(
        msg`Workspaces that saved their own provider order will lose that override and follow the platform order from now on. Their API keys and other settings are not touched.`,
      ),
      labels: { confirm: i18n._(msg`Apply to all`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "warning" },
      onConfirm: () => clearTenantOrderOverrides(),
    })
  }, [i18n, clearTenantOrderOverrides])

  const moveBatchAdapter = useCallback((from: number, to: number): void => {
    setBatchOrder((prev) => {
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })
  }, [])

  const saveBatchOrder = useCallback((): void => {
    saveFallback({ batch_fallback_order: JSON.stringify(batchOrder) })
  }, [batchOrder, saveFallback])

  const moveStreamingAdapter = useCallback((from: number, to: number): void => {
    setStreamingOrder((prev) => {
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })
  }, [])

  const saveStreamingOrder = useCallback((): void => {
    saveFallback({ streaming_fallback_order: JSON.stringify(streamingOrder) })
  }, [streamingOrder, saveFallback])

  // Logo, colour, login background, and the default theme live on the
  // organisation row, not in the settings map. Seed once from the public
  // tenant branding so a member can see them without a second request.
  const [brandingDraft, setBrandingDraft] = useState<IBrandingDraft>({
    logoUrl: "",
    brandColor: "",
    loginBackgroundUrl: "",
    defaultTheme: "",
  })
  const brandingInit = useRef(false)
  useEffect(() => {
    if (brandingInit.current) return
    if (tenantLoading) return
    brandingInit.current = true
    setBrandingDraft({
      logoUrl: branding?.logo ?? "",
      brandColor: branding?.brandColor ?? "",
      loginBackgroundUrl: branding?.loginBackground ?? "",
      defaultTheme: branding?.defaultTheme ?? "",
    })
  }, [branding, tenantLoading])

  const { mutate: saveBranding, isPending: savingBranding } = useMutation({
    mutationFn: () => {
      if (!company) return Promise.resolve(null)
      const blankToNull = (value: string): string | null => {
        const trimmed = value.trim()
        return trimmed === "" ? null : trimmed
      }
      return updateCompanyBranding(company, {
        logoUrl: blankToNull(brandingDraft.logoUrl),
        brandColor: blankToNull(brandingDraft.brandColor),
        loginBackgroundUrl: blankToNull(brandingDraft.loginBackgroundUrl),
        defaultTheme: blankToNull(brandingDraft.defaultTheme),
      })
    },
    onSuccess: () => {
      notifications.show({
        title: i18n._(msg`Saved`),
        message: i18n._(msg`Logo and brand colour updated. They show up for everyone after a refresh.`),
        color: "success",
      })
    },
    onError: (err) => notifyError(err),
  })

  return (
    <SettingsView
      product={product}
      mcpUrl={mcpUrl}
      settings={settings}
      configuredSecrets={configuredSecrets}
      updateSetting={updateSetting}
      save={save}
      saveKey={saveKey}
      saving={saving}
      savingFallback={savingFallback}
      isAdminOnly={isAdminOnly}
      canManage={canManage}
      company={company}
      isDirty={isDirty}
      isLoading={isLoading}
      error={error ? extractErrorMessage(error) : null}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      batchOrder={batchOrder}
      streamingOrder={streamingOrder}
      providerHealth={providerHealth}
      resolvedKeys={resolvedKeys}
      onMoveBatch={moveBatchAdapter}
      onSaveBatch={saveBatchOrder}
      onMoveStreaming={moveStreamingAdapter}
      onSaveStreaming={saveStreamingOrder}
      onApplyOrderToAllTenants={isAdminOnly ? confirmApplyOrderToAllTenants : undefined}
      applyingOrderToAllTenants={applyingOrderToAllTenants}
      showAppearance={showAppearance}
      showSummaryLayout={showAppearance}
      summaryLayoutSlot={showAppearance ? <SummaryLayoutPanel /> : null}
      showReportTypes={showAppearance}
      reportTypesSlot={showAppearance ? <ReportTypesPanel /> : null}
      companyName={isAdminOnly ? null : branding?.name || activeOrg?.name || null}
      branding={brandingDraft}
      onBrandingChange={setBrandingDraft}
      onSaveBranding={saveBranding}
      savingBranding={savingBranding}
    />
  )
}

export const Route = createFileRoute("/_auth/settings")({
  component: SettingsPage,
})
